import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { useT } from "../lib/i18n.jsx";
import { useToast } from "../components/Toast.jsx";
import { useAuth } from "../lib/auth.jsx";
import { IconSearch, IconPlay, IconShield } from "../components/icons.jsx";
// (DeviceManageModal removed — using inline ManageDeviceModal below)

function relativeTime(iso, t) {
  if (!iso) return t("common_never");
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export default function DevicesPage() {
  const { t } = useT();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [twoFa, setTwoFa] = useState(null);
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  const [planRequired, setPlanRequired] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function startConnect(device, password) {
    setError(null);
    try {
      const body = { device_id: device.device_id };
      if (password) body.password = password;
      const r = await api("/browser/connect/start", { method: "POST", body });
      if (r.requires_2fa) {
        setPasswordPrompt(null);
        setTwoFa({
          device,
          code_prefix: r.code_prefix,
          expires_at: r.expires_at,
        });
      } else {
        setPasswordPrompt(null);
        navigate(`/connect/${encodeURIComponent(r.session_token)}/${device.device_id}`);
      }
    } catch (err) {
      // Password required
      if (err.status === 401 && err.details?.error === "password_required") {
        setPasswordPrompt({ device, kind: err.details.password_kind, wrong: !!password });
        return;
      }
      if (err.status === 402 && err.details?.error === "active_plan_required") {
        setPlanRequired({
          device,
          currentPlan: err.details.current_plan,
          planActive: err.details.plan_active,
        });
        return;
      }
      if (err.details?.error === "browser_access_disabled") {
        toast.warn(err.details.message || "Browser-Access is temporarily disabled", { title: "Maintenance" });
        return;
      }
      if (err.status === 409) {
        toast.error("Host is offline. Make sure the Vorks Connect app is running on the target machine.", { title: "Can't connect" });
        return;
      }
      toast.error(err.message, { title: "Connect failed" });
    }
  }

  async function load() {
    try {
      const data = await api("/devices");
      setDevices(data.devices);
    } catch (err) {
      // Don't toast every poll error — only first time
      if (!devices) {
        toast.error("Couldn't load devices: " + err.message);
      }
    }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!devices) return null;
    const f = filter.trim().toLowerCase();
    if (!f) return devices;
    return devices.filter((d) =>
      d.device_name?.toLowerCase().includes(f) ||
      d.hostname?.toLowerCase().includes(f) ||
      d.device_id?.toLowerCase().includes(f) ||
      d.os?.toLowerCase().includes(f)
    );
  }, [devices, filter]);

  const stats = useMemo(() => {
    if (!devices) return null;
    return {
      total: devices.length,
      online: devices.filter((d) => d.online).length,
      linux: devices.filter((d) => d.platform === "linux").length,
      windows: devices.filter((d) => d.platform === "windows").length,
    };
  }, [devices]);

  async function toggleBrowserAccess(device, enabled) {
    try {
      await api(`/browser/devices/${device.id}/toggle`, {
        method: "POST",
        body: { enabled },
      });
      await load();
      toast.success(
        enabled
          ? `Browser-Access für ${device.device_name} aktiviert`
          : `Browser-Access für ${device.device_name} deaktiviert`
      );
    } catch (err) {
      toast.error("Toggle fehlgeschlagen: " + err.message);
    }
  }

  if (devices === null && !error) return <div className="loading">{t("common_loading")}</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">{t("devices_fleet")}</div>
          <h1 className="h1">{t("devices_title")}</h1>
        </div>
        {stats && (
          <div className="ph-meta">
            <div><strong>{stats.online}</strong> / {stats.total} {t("devices_online")}</div>
          </div>
        )}
      </div>

      {stats && (
        <div className="stats">
          <Stat label={t("devices_total")} value={stats.total} />
          <Stat label={t("devices_online")} value={stats.online} accent />
          <Stat label="Linux" value={stats.linux} />
          <Stat label="Windows" value={stats.windows} />
        </div>
      )}

      <div className="toolbar">
        <div className="search">
          <IconSearch className="search-icon" size={14} />
          <input
            type="text"
            placeholder={t("devices_search")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-line">{error}</div>}

      {filtered && filtered.length === 0 && (
        <div className="empty">
          <div className="empty-title">{t("devices_no_devices")}</div>
          <div className="empty-body">{t("devices_no_devices_sub")}</div>
        </div>
      )}

      <div className="grid">
        <AnimatePresence>
          {filtered?.map((d, i) => (
            <motion.div
              key={d.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.4), duration: 0.4 }}
              className={`card ${d.online ? "online" : "offline"}`}
            >
              <div className="card-head">
                <div>
                  <div className="card-name">{d.device_name}</div>
                  <div className="card-uuid">{d.device_id}</div>
                </div>
                <span className={`chip ${d.online ? "ok" : ""}`}>
                  <span className={`dot ${d.online ? "live" : "dead"}`} />
                  {d.online ? "live" : "offline"}
                </span>
              </div>
              <div className="chips">
                {d.os && <span className="chip">{d.os}</span>}
                {d.arch && <span className="chip">{d.arch}</span>}
                {d.hostname && <span className="chip">{d.hostname}</span>}
                {d.browser_access_enabled ? (
                  <span className="chip acc"><IconShield size={10} /> browser access</span>
                ) : null}
              </div>
              <div className="meta-line">
                <span>{t("devices_last_seen")} — {relativeTime(d.last_seen_at, t)} {t("devices_ago")}</span>
                {d.owner_name && <span>{d.owner_name}</span>}
              </div>

              {/* Browser-Access Toggle */}
              <div className="setting-row" style={{ padding: "8px 0", borderTop: "1px solid var(--line)" }}>
                <div className="label-block">
                  <div className="lbl" style={{ fontSize: 11 }}>Browser-Access</div>
                  <div className="key" style={{ fontSize: 10 }}>
                    {d.browser_access_enabled ? "enabled — connect from dashboard" : "disabled — toggle on to allow"}
                  </div>
                </div>
                <div
                  className={"toggle " + (d.browser_access_enabled ? "on" : "")}
                  onClick={() => toggleBrowserAccess(d, !d.browser_access_enabled)}
                  role="switch"
                  aria-checked={!!d.browser_access_enabled}
                >
                  <div className="knob" />
                </div>
              </div>

              <div className="actions" style={{ display: "flex", gap: 6 }}>
                <button
                  className="btn primary"
                  onClick={() => startConnect(d)}
                  disabled={!d.online || (!d.browser_access_enabled && user?.role !== "superadmin")}
                  style={{ flex: 1 }}
                  title={
                    !d.online
                      ? "Host is offline"
                      : (!d.browser_access_enabled && user?.role !== "superadmin")
                        ? "Enable browser-access first"
                        : ""
                  }
                >
                  <IconPlay size={12} /> {t("devices_connect")}
                </button>
                {(d.owner_user_id === user?.id || user?.role === "admin" || user?.role === "superadmin") && (
                  <>
                    <button
                      className="btn ghost"
                      onClick={() => setManageTarget(d)}
                      title="Sicherheit & Sharing verwalten"
                      style={{ padding: "10px 14px" }}
                    >
                      ⚙
                    </button>
                    <button
                      className="btn ghost danger"
                      onClick={() => setDeleteTarget(d)}
                      title="Gerät entfernen"
                      style={{ padding: "10px 14px" }}
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {deleteTarget && (
          <DeleteDeviceModal
            device={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirmed={(name) => {
              setDeleteTarget(null);
              toast.success(`Gerät "${name}" entfernt`);
              load();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {manageTarget && (
          <ManageDeviceModal
            device={manageTarget}
            onClose={() => { setManageTarget(null); load(); }}
            toast={toast}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {twoFa && (
          <TwoFaModal
            device={twoFa.device}
            codePrefix={twoFa.code_prefix}
            expiresAt={twoFa.expires_at}
            onClose={() => setTwoFa(null)}
            onSuccess={(token) => {
              setTwoFa(null);
              navigate(`/connect/${encodeURIComponent(token)}/${twoFa.device.device_id}`);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {passwordPrompt && (
          <PasswordPromptModal
            device={passwordPrompt.device}
            kind={passwordPrompt.kind}
            wrong={passwordPrompt.wrong}
            onCancel={() => setPasswordPrompt(null)}
            onSubmit={(pw) => startConnect(passwordPrompt.device, pw)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {planRequired && (
          <PlanRequiredModal
            currentPlan={planRequired.currentPlan}
            planActive={planRequired.planActive}
            onClose={() => setPlanRequired(null)}
            onUpgrade={() => {
              setPlanRequired(null);
              navigate("/billing");
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function TwoFaModal({ device, codePrefix, expiresAt, onClose, onSuccess }) {
  const { t } = useT();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
  const min = Math.floor(remaining / 60);
  const sec = remaining % 60;

  async function submit(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code");
      return;
    }
    setBusy(true); setError(null);
    try {
      const r = await api("/browser/connect/verify", {
        method: "POST",
        body: { device_id: device.device_id, code },
      });
      onSuccess(r.session_token);
    } catch (err) {
      setError(err.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(12px)", display: "flex", alignItems: "center",
        justifyContent: "center", zIndex: 200, padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--acc)",
          borderRadius: "var(--r-lg)",
          padding: 36, maxWidth: 440, width: "100%",
          boxShadow: "0 0 0 1px var(--acc-glow), 0 32px 100px rgba(0,0,0,0.8)",
          position: "relative", overflow: "hidden",
        }}
      >
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, var(--acc), transparent)",
        }} />
        <div className="eyebrow">Two-factor verification</div>
        <h2 style={{ fontSize: 22, fontWeight: 600, margin: "8px 0 4px" }}>
          Code sent
        </h2>
        <p className="dim" style={{ fontSize: 13, marginTop: 0 }}>
          A 6-digit code was sent to your email and Discord (if linked). Enter
          it below to start the connection to <strong style={{ color: "var(--text)" }}>{device.device_name}</strong>.
        </p>
        <p className="mono" style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
          Hint: code starts with <strong style={{ color: "var(--acc)" }}>{codePrefix}</strong> · expires in {min}:{String(sec).padStart(2, "0")}
        </p>

        <form onSubmit={submit}>
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
            disabled={busy}
            placeholder="123456"
            style={{
              fontSize: 32, letterSpacing: "0.25em",
              textAlign: "center", fontFamily: "var(--mono)",
              fontWeight: 700, margin: "20px 0",
              padding: "16px 12px",
              color: "var(--acc)",
            }}
          />
          {error && <div className="error-line">{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy} style={{ flex: 1 }}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || code.length !== 6} style={{ flex: 2 }}>
              {busy ? "Verifying…" : "Verify & connect"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={"stat-value" + (accent ? " acc" : "")}>{value}</div>
    </div>
  );
}

function DeleteDeviceModal({ device, onClose, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await api(`/devices/${device.id}`, { method: "DELETE" });
      onConfirmed(device.device_name);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}
      onClick={() => !busy && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--err)",
          borderRadius: "var(--r-lg)",
          padding: 32,
          maxWidth: 440, width: "100%",
          boxShadow: "0 32px 80px rgba(0,0,0,0.8)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow" style={{ color: "var(--err)" }}>Achtung</div>
        <h2 style={{ fontSize: 20, margin: "8px 0 12px" }}>Gerät entfernen?</h2>
        <p style={{ color: "var(--text-dim)", fontSize: 13, marginBottom: 18 }}>
          <strong style={{ color: "var(--text)" }}>{device.device_name}</strong> wird aus deinem Account entfernt.
          Auf dem Gerät selbst läuft die App weiter, aber sie ist nicht mehr verbunden.
        </p>
        <div className="mono" style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 18, lineHeight: 1.6 }}>
          UUID: {device.device_id}<br />
          OS: {device.os || "—"} • Hostname: {device.hostname || "—"}
        </div>
        {error && <div className="error-line" style={{ marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button className="btn danger" onClick={confirm} disabled={busy}>
            {busy ? "Entferne…" : "Endgültig entfernen"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PlanRequiredModal({ currentPlan, planActive, onClose, onUpgrade }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--acc)",
          borderRadius: "var(--r-lg)",
          padding: 36,
          maxWidth: 460, width: "100%",
          boxShadow: "0 0 0 1px var(--acc-glow), 0 32px 80px rgba(0,0,0,0.8)",
          position: "relative", overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent, var(--acc), transparent)",
        }} />

        <div style={{
          width: 56, height: 56,
          borderRadius: "50%",
          border: "1px solid var(--acc)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 18,
          fontSize: 24,
          color: "var(--acc)",
          boxShadow: "0 0 20px var(--acc-glow)",
        }}>
          ★
        </div>

        <div className="eyebrow">Active plan required</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: "8px 0 12px" }}>
          Upgrade um über den Browser zu verbinden
        </h2>
        <p style={{ color: "var(--text-dim)", fontSize: 13, lineHeight: 1.55, marginBottom: 20 }}>
          Der Vorks Connect Browser-Zugriff (Verbinden über das Dashboard) ist Teil
          der kostenpflichtigen Pläne. Mit deinem aktuellen Plan kannst du den Vorks
          Connect Desktop-Client weiterhin uneingeschränkt nutzen.
        </p>

        <div style={{
          background: "var(--bg)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r)",
          padding: "12px 14px",
          marginBottom: 22,
          fontFamily: "var(--mono)",
          fontSize: 11,
          color: "var(--text-dim)",
        }}>
          Aktueller Plan: <strong style={{ color: "var(--text)" }}>{currentPlan || "Free"}</strong>
          {!planActive && currentPlan && currentPlan !== "free" && (
            <span style={{ color: "var(--err)", marginLeft: 8 }}>(abgelaufen)</span>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn ghost" onClick={onClose}>Später</button>
          <button className="btn primary" onClick={onUpgrade}>
            Plan ansehen →
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function PasswordPromptModal({ device, kind, wrong, onCancel, onSubmit }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function handle(e) {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    await onSubmit(password);
    setBusy(false);
  }

  const subtitle = {
    device: "This device is password-protected. Enter the device password to continue.",
    share:  "This device was shared with you. Enter the password you were given.",
    guest:  "Enter the guest-link password to continue.",
  }[kind] || "Enter the password to continue.";

  return (
    <motion.div
      className="modal-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="modal"
        initial={{ scale: 0.94, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.96 }}
        onClick={(e) => e.stopPropagation()}
        style={{ minWidth: 380, maxWidth: 460 }}
      >
        <form onSubmit={handle}>
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>
            Password required
          </h2>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 6 }}>
            {device.device_name}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-mute)", marginBottom: 16, lineHeight: 1.5 }}>
            {subtitle}
          </div>

          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            disabled={busy}
            style={{
              width: "100%",
              padding: "10px 12px",
              background: "var(--bg-elev)",
              border: wrong ? "1px solid var(--err)" : "1px solid var(--line)",
              borderRadius: "var(--r)",
              color: "var(--text)",
              fontSize: 13,
              fontFamily: "var(--mono)",
              marginBottom: 6,
            }}
          />
          {wrong && (
            <div style={{ fontSize: 11, color: "var(--err)", marginBottom: 12 }}>
              Wrong password — try again.
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={busy || !password}>
              {busy ? "Verifying…" : "Connect"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

/**
 * ManageDeviceModal — password, sharing, guest-links.
 * Three tabs with their own state. All API calls are scoped to a single
 * device by UUID.
 */
function ManageDeviceModal({ device, onClose, toast }) {
  const [tab, setTab] = useState("password");

  return (
    <motion.div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
               display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8,
                 width: "min(640px, 92vw)", maxHeight: "85vh", display: "flex", flexDirection: "column",
                 boxShadow: "0 24px 60px rgba(0,0,0,0.55)" }}
        initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }}
      >
        <div style={{ padding: "18px 22px 0", borderBottom: "1px solid var(--line)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 600 }}>{device.device_name}</div>
              <div style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--mono)" }}>{device.device_id}</div>
            </div>
            <button onClick={onClose}
              style={{ background: "transparent", border: "none", color: "var(--text-dim)", fontSize: 20, cursor: "pointer" }}>×</button>
          </div>
          <div style={{ display: "flex", gap: 18, marginTop: 12 }}>
            <TabBtn active={tab === "password"} onClick={() => setTab("password")}>Password</TabBtn>
            <TabBtn active={tab === "shares"} onClick={() => setTab("shares")}>Sharing</TabBtn>
            <TabBtn active={tab === "guest"} onClick={() => setTab("guest")}>Guest Links</TabBtn>
          </div>
        </div>

        <div style={{ padding: 22, overflowY: "auto" }}>
          {tab === "password" && <PasswordTab device={device} toast={toast} />}
          {tab === "shares" && <SharesTab device={device} toast={toast} />}
          {tab === "guest" && <GuestLinksTab device={device} toast={toast} />}
        </div>
      </motion.div>
    </motion.div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent", border: "none",
        color: active ? "var(--text)" : "var(--text-dim)",
        padding: "10px 0", fontSize: 12,
        fontFamily: "inherit", cursor: "pointer",
        borderBottom: active ? "2px solid var(--acc)" : "2px solid transparent",
        letterSpacing: "0.04em", textTransform: "uppercase",
        fontWeight: active ? 600 : 400,
        transition: "color 0.15s",
      }}
    >{children}</button>
  );
}

function PasswordTab({ device, toast }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasPassword, setHasPassword] = useState(null);

  useEffect(() => {
    api(`/devices/${device.device_id}/password`)
      .then((r) => setHasPassword(!!r.has_password))
      .catch(() => setHasPassword(false));
  }, [device.device_id]);

  async function save() {
    if (password.length < 4) {
      toast.error("Mindestens 4 Zeichen");
      return;
    }
    setBusy(true);
    try {
      await api(`/devices/${device.device_id}/password`, { method: "PUT", body: { password } });
      toast.success("Passwort gesetzt");
      setHasPassword(true);
      setPassword("");
    } catch (err) {
      toast.error("Fehler: " + err.message);
    } finally { setBusy(false); }
  }

  async function clear() {
    setBusy(true);
    try {
      await api(`/devices/${device.device_id}/password`, { method: "PUT", body: { clear: true } });
      toast.success("Passwort entfernt");
      setHasPassword(false);
      setPassword("");
    } catch (err) {
      toast.error("Fehler: " + err.message);
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 14, lineHeight: 1.6 }}>
        Optionales Passwort das jeder eingeben muss um zu connecten — außer dem Owner.<br/>
        Admins sehen die Abfrage, können sie aber überspringen. Superadmin überspringt automatisch.
      </div>

      {hasPassword === true && (
        <div style={{ padding: 10, background: "rgba(74,222,128,0.08)",
                      border: "1px solid rgba(74,222,128,0.25)", borderRadius: 5,
                      marginBottom: 14, fontSize: 12 }}>
          🔒 Passwort ist aktiv
        </div>
      )}
      {hasPassword === false && (
        <div style={{ padding: 10, background: "rgba(255,255,255,0.03)",
                      border: "1px solid var(--line)", borderRadius: 5,
                      marginBottom: 14, fontSize: 12, color: "var(--text-dim)" }}>
          Aktuell kein Passwort gesetzt
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: "var(--text-dim)", display: "block", marginBottom: 4 }}>
          Neues Passwort
        </label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="mindestens 4 Zeichen"
          style={{ width: "100%", padding: "10px 12px",
                   background: "var(--bg-elev)", border: "1px solid var(--line)",
                   borderRadius: 5, color: "var(--text)", fontSize: 13 }}
        />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn primary" disabled={busy || !password} onClick={save}>
          {hasPassword ? "Ändern" : "Setzen"}
        </button>
        {hasPassword && (
          <button className="btn ghost danger" disabled={busy} onClick={clear}>
            Entfernen
          </button>
        )}
      </div>
    </div>
  );
}

function SharesTab({ device, toast }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api(`/devices/${device.device_id}/shares`);
      setShares(r.shares || []);
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [device.device_id]);

  async function addShare() {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api(`/devices/${device.device_id}/shares`, {
        method: "POST", body: { grantee_email: email.trim() },
      });
      toast.success(`Geteilt mit ${email}`);
      setEmail("");
      await load();
    } catch (err) {
      toast.error("Fehler: " + err.message);
    } finally { setBusy(false); }
  }

  async function revoke(id) {
    try {
      await api(`/shares/${id}`, { method: "DELETE" });
      toast.success("Share zurückgezogen");
      await load();
    } catch (err) {
      toast.error("Fehler: " + err.message);
    }
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 14, lineHeight: 1.6 }}>
        Teile dieses Gerät mit anderen Vorks-Connect-Nutzern. Sie sehen es dann in ihrer "My Devices"-Liste.
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          onKeyDown={(e) => e.key === "Enter" && addShare()}
          style={{ flex: 1, padding: "10px 12px",
                   background: "var(--bg-elev)", border: "1px solid var(--line)",
                   borderRadius: 5, color: "var(--text)", fontSize: 13 }}
        />
        <button className="btn primary" disabled={busy || !email} onClick={addShare}>
          Teilen
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 20 }}>…</div>
      ) : shares.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 20, fontSize: 12 }}>
          Noch nicht geteilt
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {shares.map((s) => (
            <div key={s.id}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                       padding: "10px 12px", background: "var(--bg-elev)",
                       border: "1px solid var(--line)", borderRadius: 5 }}>
              <div>
                <div style={{ fontSize: 13 }}>{s.grantee_name || s.grantee_email}</div>
                <div style={{ fontSize: 10, color: "var(--text-dim)" }}>
                  {s.grantee_email} · seit {new Date(s.created_at).toLocaleDateString()}
                  {s.has_password && " · 🔒 eigenes Passwort"}
                  {s.expires_at && ` · läuft ab ${new Date(s.expires_at).toLocaleDateString()}`}
                </div>
              </div>
              <button
                onClick={() => revoke(s.id)}
                style={{ background: "transparent", border: "none",
                         color: "var(--err)", cursor: "pointer", fontSize: 12 }}
              >Entfernen</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GuestLinksTab({ device, toast }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newLink, setNewLink] = useState({ label: "", password: "", expires_hours: "", max_uses: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api(`/devices/${device.device_id}/guest-links`);
      setLinks(r.links || []);
    } catch {} finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [device.device_id]);

  async function createLink() {
    setBusy(true);
    try {
      const body = { label: newLink.label || null };
      if (newLink.password) body.password = newLink.password;
      if (newLink.max_uses) body.max_uses = parseInt(newLink.max_uses, 10);
      if (newLink.expires_hours) {
        const hrs = parseInt(newLink.expires_hours, 10);
        if (hrs > 0) {
          body.expires_at = new Date(Date.now() + hrs * 3600_000).toISOString();
        }
      }
      const r = await api(`/devices/${device.device_id}/guest-links`, { method: "POST", body });
      toast.success("Guest Link erstellt");
      // Show the URL with a copy button
      const url = `${window.location.origin}/guest/${r.token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Link kopiert"); } catch {}
      setNewLink({ label: "", password: "", expires_hours: "", max_uses: "" });
      setShowCreate(false);
      await load();
    } catch (err) {
      toast.error("Fehler: " + err.message);
    } finally { setBusy(false); }
  }

  async function revoke(id) {
    try {
      await api(`/guest-links/${id}`, { method: "DELETE" });
      toast.success("Link revoked");
      await load();
    } catch (err) {
      toast.error("Fehler: " + err.message);
    }
  }

  function copyLink(token) {
    const url = `${window.location.origin}/guest/${token}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link kopiert"),
      () => toast.error("Kopieren fehlgeschlagen"),
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 14, lineHeight: 1.6 }}>
        Guest-Links erlauben temporären Zugriff ohne Vorks-Connect-Account.
      </div>

      {!showCreate ? (
        <button className="btn primary" onClick={() => setShowCreate(true)} style={{ marginBottom: 16 }}>
          + Neuer Guest Link
        </button>
      ) : (
        <div style={{ padding: 14, background: "var(--bg-elev)", border: "1px solid var(--line)",
                      borderRadius: 5, marginBottom: 16 }}>
          <input
            value={newLink.label}
            onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
            placeholder="Label (optional, z.B. 'Support session Mike')"
            style={inputStyle}
          />
          <input
            type="password"
            value={newLink.password}
            onChange={(e) => setNewLink({ ...newLink, password: e.target.value })}
            placeholder="Passwort (optional)"
            style={inputStyle}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="number"
              value={newLink.expires_hours}
              onChange={(e) => setNewLink({ ...newLink, expires_hours: e.target.value })}
              placeholder="Stunden bis Ablauf"
              style={inputStyle}
            />
            <input
              type="number"
              value={newLink.max_uses}
              onChange={(e) => setNewLink({ ...newLink, max_uses: e.target.value })}
              placeholder="Max Uses (leer = ∞)"
              style={inputStyle}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn primary" disabled={busy} onClick={createLink}>Erstellen + Link kopieren</button>
            <button className="btn ghost" onClick={() => setShowCreate(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 20 }}>…</div>
      ) : links.length === 0 ? (
        <div style={{ textAlign: "center", color: "var(--text-dim)", padding: 20, fontSize: 12 }}>
          Keine Guest Links
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {links.map((l) => {
            const expired = l.expires_at && new Date(l.expires_at).getTime() < Date.now();
            const exhausted = l.max_uses && l.used_count >= l.max_uses;
            const usable = !expired && !exhausted && !l.revoked_at;
            return (
              <div key={l.id}
                style={{ padding: "10px 12px", background: "var(--bg-elev)",
                         border: "1px solid var(--line)", borderRadius: 5,
                         opacity: usable ? 1 : 0.55 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>{l.label || "Unbenannt"}</div>
                    <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>
                      {l.has_password && "🔒 "}
                      {l.used_count}{l.max_uses ? ` / ${l.max_uses}` : ""} uses
                      {l.expires_at && ` · ${expired ? "abgelaufen" : `läuft ab ${new Date(l.expires_at).toLocaleString()}`}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {usable && (
                      <button onClick={() => copyLink(l.token)}
                        style={{ background: "transparent", border: "1px solid var(--line)",
                                 color: "var(--text)", padding: "4px 10px",
                                 borderRadius: 4, fontSize: 11, cursor: "pointer" }}>
                        Copy
                      </button>
                    )}
                    <button onClick={() => revoke(l.id)}
                      style={{ background: "transparent", border: "none",
                               color: "var(--err)", cursor: "pointer", fontSize: 11, padding: "4px 8px" }}>
                      Revoke
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 10px",
  background: "var(--bg)", border: "1px solid var(--line)",
  borderRadius: 4, color: "var(--text)", fontSize: 12,
  marginBottom: 8,
};
